import tinycolor from 'tinycolor2';
import {
  defaultConfiguration,
  defaultInfoCardConfiguration,
  defaultTableConfiguration,
} from '../../constants/constants';
import { TABLE_STYLE_SCHEMA } from '../tables/tableStyleSchema';
import { paletteColor } from './palette';
import { classicValuesFromSite } from './styleSchema';
import { THEME_STYLE_LISTS } from './themeStyles';
import { styleListKeys } from './styleList';

/**
 * Le thème d'un site est une `configuration` du classique, réduite à ce que le
 * site sait appliquer : on l'édite avec le configurateur du classique (mêmes
 * onglets, mêmes champs), et un style du document et un style du thème ont la
 * même forme — la correspondance à la génération compare des choses
 * comparables.
 *
 * `theme.configuration` la porte. Les thèmes d'avant (palette `colors`,
 * `typography`, `componentStyles` au format web) sont relus à la volée ; on
 * n'écrit plus que le nouveau format.
 */

const STYLE_KEYS = [...new Set(Object.values(THEME_STYLE_LISTS))].map(styleListKeys);

export const THEME_CONFIGURATION_KEYS = [
  'colors',
  'fontFamily',
  'fontFamilyList',
  'fontSize',
  'baseFontColor',
  'title1',
  'title2',
  'title3',
  'title4',
  ...STYLE_KEYS.flatMap(({ list, idCount, defaultId }) => [list, idCount, defaultId]),
];

export function pickThemeConfiguration(configuration) {
  return Object.fromEntries(THEME_CONFIGURATION_KEYS
    .filter((key) => configuration?.[key] !== undefined)
    .map((key) => [key, configuration[key]]));
}

// Pas les styles d'éléments du classique : un thème n'impose aucun style tant
// qu'on n'en crée pas. Listes vides explicites, pour couvrir celles de
// `defaultConfiguration` là où on les fusionne (configurateur).
const DEFAULTS = {
  ...pickThemeConfiguration(defaultConfiguration),
  ...Object.fromEntries(STYLE_KEYS.flatMap(({ list, idCount }) => [[list, []], [idCount, 0]])),
};

/** Police par défaut d'un site : celle d'un document neuf du classique. */
export const DEFAULT_SITE_FONT = defaultConfiguration.fontFamily;

/**
 * Police que le web posait par défaut avant d'adopter celle du classique :
 * des blocs enregistrés la portent sans l'avoir choisie.
 */
export const LEGACY_DEFAULT_FONT = 'Inter';

const LEGACY_KEYS = ['typography', 'componentStyles'];

/** Le thème dit-il quoi que ce soit du texte et des composants du site ? */
export function isThemeConfigured(theme) {
  return !!theme?.configuration || LEGACY_KEYS.some((key) => !!theme?.[key]);
}

const stripFallback = (fontFamily) => String(fontFamily || '').split(',')[0].replace(/["']/g, '').trim();

// Même couleur sous deux écritures (« #ffffff » de la palette d'avant,
// « rgba(255,255,255, 1.0) » du classique) : comparer les valeurs, pas le texte.
const colorKey = (style) => {
  const color = tinycolor(style);
  return color.isValid() ? color.toHex8String() : String(style);
};

/** Une pastille par couleur : la première écriture gagne (son libellé avec). */
function withoutDuplicateColors(colors) {
  const seen = new Set();
  return (colors || []).filter((color) => {
    if (!color?.style) return false;
    const key = colorKey(color.style);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function legacyColors(theme) {
  const legacy = Array.isArray(theme?.colors)
    ? theme.colors.map((color) => ({ label: color?.name || color?.value, style: color?.value }))
    : (theme?.richTextCustomColors || []).map((value) => ({ label: value, style: value }));
  return withoutDuplicateColors([...DEFAULTS.colors, ...legacy]);
}

function legacyTitle(level, typography) {
  const title = typography?.[level] || {};
  return {
    ...DEFAULTS[level],
    ...(title.fontFamily ? { fontFamily: title.fontFamily } : {}),
    ...(title.fontSize ? { fontSize: title.fontSize } : {}),
    ...(title.fontWeight ? { fontWeight: title.fontWeight } : {}),
    ...(title.color ? { color: paletteColor(title.color) } : {}),
  };
}

/**
 * Style de tableau du site (valeurs de `design`) → style du configurateur,
 * réglage par réglage selon TABLE_STYLE_SCHEMA (inverse de
 * `proposalTableDesign`). `base` : le style qu'on retouche, dont les réglages
 * que le site ne connaît pas restent.
 */
export function classicTableStyleFromDesign(design, { id, name }, base = defaultTableConfiguration) {
  return {
    ...base,
    id,
    name,
    ...classicValuesFromSite(TABLE_STYLE_SCHEMA, design),
  };
}

/**
 * Thème au format d'avant → configuration. Les identifiants des styles sont
 * gardés : des blocs les référencent (`themeStyleId`).
 */
function legacyThemeConfiguration(theme) {
  const typography = theme?.typography || {};
  const body = typography.body || {};
  const fontFamily = body.fontFamily || stripFallback(theme?.fontFamily) || DEFAULTS.fontFamily;
  const tables = theme?.componentStyles?.table || [];
  const cards = theme?.componentStyles?.infoCard || [];

  return {
    ...DEFAULTS,
    colors: legacyColors(theme),
    fontFamily,
    fontFamilyList: [{ key: fontFamily.replace(/\s+/g, '_'), familyName: fontFamily }],
    ...(body.fontSize ? { fontSize: body.fontSize } : {}),
    ...(body.color ? { baseFontColor: paletteColor(body.color) } : {}),
    title1: legacyTitle('title1', typography),
    title2: legacyTitle('title2', typography),
    title3: legacyTitle('title3', typography),
    title4: legacyTitle('title4', typography),
    ...(tables.length ? {
      tableStyles: tables.map((style) => classicTableStyleFromDesign(style.props, style)),
      tableStylesIdCount: tables.length,
    } : {}),
    ...(cards.length ? {
      infoCardStyles: cards.map((style) => ({
        ...defaultInfoCardConfiguration, ...style.props, id: style.id, name: style.name,
      })),
      infoCardStylesIdCount: cards.length,
    } : {}),
  };
}

/** La configuration du thème, complétée par les défauts du classique. */
export function readThemeConfiguration(theme) {
  if (theme?.configuration) {
    const configuration = { ...DEFAULTS, ...pickThemeConfiguration(theme.configuration) };
    return { ...configuration, colors: withoutDuplicateColors(configuration.colors) };
  }
  return legacyThemeConfiguration(theme);
}

/** Le thème réécrit au nouveau format : les clés d'avant disparaissent. */
export function withThemeConfiguration(theme, configuration) {
  const { typography, componentStyles, richTextCustomColors, colors, ...rest } = theme || {};
  return { ...rest, configuration: pickThemeConfiguration(configuration) };
}
